/**
 * @fileoverview One collapsible party group in the conflict-check schedule
 * (MOR-882, MOR-992)
 *
 * Purpose: a group header that answers the conflict question before it is
 * opened — the party count plus a PMSI / non-PMSI tally — with the party
 * table beneath.
 *
 * Small groups open by default: the table is compact enough that a voter
 * should not have to click to see a dozen names. Large groups start
 * collapsed, since one can run to hundreds of parties and the brief is the
 * thing to read.
 *
 * Used by: conflict-party-schedule.tsx
 */

import { useEffect, useRef, useState } from "react";
import { ChevronRight } from "lucide-react";

import { Badge } from "@/ui/primitives/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/ui/composed/sections/collapsible";
import { cn } from "@/utils/misc";
import { ConflictPartyTable } from "./conflict-party-table";
import {
  resolveParties,
  tallyRegistrations,
  type ConflictPartyListView,
} from "./conflict-party-schedule.helpers";

/** Groups up to this many parties start expanded. */
const OPEN_BY_DEFAULT_MAX_PARTIES = 12;

interface ConflictPartyGroupProps {
  list: ConflictPartyListView;
}

/**
 * @component
 * @example
 * <ConflictPartyGroup list={list} />
 */
export function ConflictPartyGroup({ list }: ConflictPartyGroupProps) {
  const parties = resolveParties(list);
  const [open, setOpen] = useState(
    parties.length <= OPEN_BY_DEFAULT_MAX_PARTIES,
  );
  // The list can keep growing while PPSR processing continues (MOR-992), so
  // the initial-render size alone isn't enough to keep a group manageable.
  // Collapse it the FIRST time it crosses the threshold after mount, then
  // leave it alone — a practitioner who reopens it to watch the list fill in
  // should not be fought back into collapsed on every subsequent update.
  const collapsedForGrowth = useRef(false);
  useEffect(() => {
    if (
      !collapsedForGrowth.current &&
      parties.length > OPEN_BY_DEFAULT_MAX_PARTIES
    ) {
      collapsedForGrowth.current = true;
      setOpen(false);
    }
  }, [parties.length]);
  const tally = tallyRegistrations(parties);
  const hasDetail = tally.pmsi + tally.nonPmsi > 0;

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="grid gap-1.5">
      <CollapsibleTrigger className="flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left text-xs transition-colors hover:bg-muted">
        <ChevronRight
          className={cn(
            "size-3.5 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-90",
          )}
        />
        <span className="font-medium">{list.label}</span>
        <span className="text-muted-foreground">{parties.length}</span>
        {hasDetail && (
          <span className="ml-auto flex shrink-0 items-center gap-1">
            {tally.pmsi > 0 && (
              <Badge color="green" variant="soft" size="sm">
                {tally.pmsi} PMSI
              </Badge>
            )}
            {tally.nonPmsi > 0 && (
              <Badge color="amber" variant="soft" size="sm">
                {tally.nonPmsi} Non-PMSI
              </Badge>
            )}
          </span>
        )}
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ConflictPartyTable parties={parties} hasDetail={hasDetail} />
      </CollapsibleContent>
    </Collapsible>
  );
}
